import { useState } from "react";
import { useSession } from "@/hooks/useSession";
import { usePatient } from "@/hooks/usePatient";
import { useTreatments } from "@/hooks/useTreatments";
import { useAssessments } from "@/hooks/useAssessments";
import { AppLayout } from "@/components/Layout";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/Card";
import { EmptyState } from "@/components/EmptyState";
import { LoadingScreen } from "@/components/LoadingScreen";
import { ShopIcon } from "@/components/icons";
import {
  CATALOG, VERTICAL_LABEL, WAITLIST_URL, suggestedProducts, type ShopProduct,
} from "@/data/shopCatalog";

function ProductCTA({ product, compact = false }: { product: ShopProduct; compact?: boolean }) {
  const cls = compact
    ? "inline-flex items-center justify-center rounded-[8px] px-3 py-1.5 text-[12px] font-semibold"
    : "inline-flex items-center justify-center rounded-[9px] px-4 py-2 text-[13px] font-semibold";
  return product.available && product.assessmentUrl ? (
    <a
      href={product.assessmentUrl}
      target="_blank"
      rel="noopener noreferrer"
      className={`${cls} bg-fh-green text-white hover:bg-fh-green-dark`}
    >
      Start assessment →
    </a>
  ) : (
    <a
      href={WAITLIST_URL}
      target="_blank"
      rel="noopener noreferrer"
      className={`${cls} bg-white border border-fh-border text-fh-ink hover:bg-fh-cream`}
    >
      Join waitlist
    </a>
  );
}

function VerticalChip({ product }: { product: ShopProduct }) {
  return (
    <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[10.5px] font-semibold uppercase tracking-wider bg-fh-sand text-fh-green-dark">
      {VERTICAL_LABEL[product.vertical]}
    </span>
  );
}

export default function Shop() {
  const { session } = useSession();
  const patientQ = usePatient(session?.user.id);
  const treatmentsQ = useTreatments(patientQ.data?.id);
  const assessmentsQ = useAssessments(patientQ.data?.id);
  const [query, setQuery] = useState("");

  if (patientQ.isLoading) return <LoadingScreen />;

  const suggestions = suggestedProducts(treatmentsQ.data ?? [], assessmentsQ.data ?? []);

  const q = query.trim().toLowerCase();
  const results = q
    ? CATALOG.filter((p) =>
        [p.name, p.tagline, p.detail, VERTICAL_LABEL[p.vertical]].join(" ").toLowerCase().includes(q),
      )
    : CATALOG;

  return (
    <AppLayout>
      <PageHeader
        title="Shop"
        subtitle="Treatments and support that complement your plan. Prescription items always start with a clinical assessment."
      />

      {/* Suggested for you — cross-sell ranked by the patient's active plan + goals. */}
      <Card title="Suggested for you" className="mb-4" action={
        <span className="text-[11px] font-semibold uppercase tracking-wider text-fh-gray">
          Based on your plan
        </span>
      }>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {suggestions.map((p) => (
            <div key={p.id} className="rounded-[12px] border border-fh-border bg-fh-cream p-3.5 flex flex-col">
              <VerticalChip product={p} />
              <div className="text-[14.5px] font-semibold text-fh-ink mt-2">{p.name}</div>
              <div className="text-[12px] text-fh-gray mb-2">{p.tagline}</div>
              <div className="mt-auto flex items-center justify-between gap-2">
                <span className="text-[12px] font-semibold text-fh-green-dark">{p.priceLabel}</span>
                <ProductCTA product={p} compact />
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Search */}
      <div className="mb-4">
        <label htmlFor="shop-search" className="sr-only">Search products</label>
        <input
          id="shop-search"
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search treatments — semaglutide, NAD+, hair…"
          className="w-full rounded-[12px] border border-fh-border bg-white px-4 py-3 text-[15px] text-fh-ink placeholder:text-fh-gray-light focus:outline-none focus:border-fh-green focus-visible:ring-2 focus-visible:ring-fh-green/30"
        />
        <p className="text-[12px] text-fh-gray mt-1.5">
          {results.length} product{results.length === 1 ? "" : "s"}
        </p>
      </div>

      {results.length === 0 ? (
        <EmptyState
          icon={<ShopIcon />}
          title="No products found"
          body="Try a different keyword — or browse everything by clearing your search."
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {results.map((p) => (
            <Card key={p.id}>
              <div className="flex items-start justify-between gap-3 mb-2">
                <VerticalChip product={p} />
                <span className="text-[12.5px] font-semibold text-fh-green-dark shrink-0">
                  {p.priceLabel}
                </span>
              </div>
              <div className="text-[16px] font-semibold text-fh-ink">{p.name}</div>
              <div className="text-[13px] text-fh-gray mb-2">{p.tagline}</div>
              <p className="text-[13.5px] text-fh-ink leading-snug mb-4">{p.detail}</p>
              <div className="flex items-center justify-between gap-3">
                <ProductCTA product={p} />
                {!p.available && (
                  <span className="text-[11.5px] text-fh-gray-light">Not yet available</span>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}

      <p className="text-[11.5px] text-fh-gray-light mt-6 leading-snug">
        Prescription products require an evaluation by a licensed clinician and are only
        prescribed when clinically appropriate. Compounded medications are prepared by a
        state-licensed pharmacy and are not FDA-approved.
      </p>
    </AppLayout>
  );
}
